import IframeClient from 'iframe-client';
import { extend, runCallback, log, timestamp } from './util';
import installGoogleAnalytics from './ga';

export default class Host {
  constructor(setup) {
    // Embed descriptor (sends to host)
    this.embed = {
      type: setup.type,
      env: setup.env,
    };

    // Host descriptor (received from host)
    this.host = {
      // specifies the full url of the hosting Chorus page (uses local url by default).
      url: location.href,

      // specifies the domain of the hosting Chorus page (uses local origin by default).
      origin: location.origin,

      // specifies the referrer of the hosting Chorus page.
      referrer: '',

      // specifies if the local document (video player) is running in an iframe.
      iframe: IframeClient.isInIframe(),

      // specifies the title of the hosting Chorus page.
      title: null,

      // specifies if this player has connected to a hosting Chorus page.
      api: false
    };

    this.xframe = IframeClient.create('volume', '*');
  }

  /**
  * Posts a message to the parent window.
  * @param {String|Object} message string or formatted object to send.
  * Passing an object requires that a "message" key be provided.
  * @param {String} [value] an optional value to send with the message.
  */
  post(message, value) {
    this.logSend(message, value);
    this.xframe.post(window.parent, message, value);
  }

  /**
  * Posts a message to the host page with request for confirmation.
  * This implementation polls the host frame with the posted message
  * until the host sends back a confirmation response.
  * Use this method to (better) guarentee delivery.
  * @param {String} message string to send.
  * @param {Any} [value] an optional value to send with the message.
  * @param {Function} [callback] an optional function to handle response.
  * @param {Number} [timeout] an optional milliseconds max to run the request for.
  */
  request(message, value, callback, timeout) {
    this.logSend(message, value);
    this.xframe.request(window.parent, message, value, callback, timeout);
  }

  /**
  * Requests data about the current Volume host.
  * This data is fulfilled asynchronously to accomodate fetching delays.
  * @param {Function} callback to run with resolved host information.
  */
  ping(callback) {
    if (this.host.iframe && this.pingQueue === undefined) {
      this.pingQueue = [callback];

      this.request('host', this.embed, (err, data) => {
        extend(this.host, data || {});
        log('EMBED-RECEIVE', 'host', `[${timestamp()}]`, this.host);
        if (!this.host.api) installGoogleAnalytics();
        this.pingQueue.forEach(cb => runCallback(cb, this.host));
        this.pingQueue = null;
      }, 8000);
    } else if (this.pingQueue) {
      // Queue response to data loading:
      this.pingQueue.push(callback);
    } else {
      // Fulfill request directly (following async pattern):
      setTimeout(() => runCallback(callback, this.host), 0);
    }
  }

  // Logs a sent message with a standard banner
  logSend(message, value) {
    const banner = ['EMBED-SEND', message, `[${timestamp()}]`].join(' ');
    if (value) {
      log(banner, value);
    } else {
      log(banner);
    }
  }

  // Registers an event on the xframe client.
  // Returns self to support chaining.
  on(eventName, callback) {
    this.xframe.on(eventName, callback);
    return this;
  }

  // Activates the xframe client,
  // then sends a ping to kickoff connection.
  connect() {
    this.xframe.listen();
    this.ping();
    return this;
  }
}
